import React, { useCallback, useEffect, useMemo, useState } from "react";
import AppShell from "../components/AppShell";
import ChallengeCard from "../components/ChallengeCard";
import ChallengeDetail from "../components/ChallengeDetail";
import RangeFilter, { passesRange } from "../components/RangeFilter";
import { listChallenges, getContext, downloadBulkXlsxUrl, bulkExportZipUrl } from "../lib/api";
import { toast } from "sonner";
import { FileXls, FileZip } from "@phosphor-icons/react";

const FILTERS = [
    { id: "all", label: "All" },
    { id: "draft", label: "Drafts" },
    { id: "ready_for_review", label: "Review" },
    { id: "community_review", label: "Community" },
    { id: "published", label: "Published" },
    { id: "archived", label: "Archived" },
];

export default function Library() {
    const [items, setItems] = useState([]);
    const [filter, setFilter] = useState("all");
    const [selected, setSelected] = useState(null);
    const [context, setContext] = useState(null);
    const [loading, setLoading] = useState(true);
    const [qualityRange, setQualityRange] = useState({ selectedMin: 0, selectedMax: 100 });

    const filteredItems = useMemo(() => {
        const f = { ...qualityRange, isEnabled: true };
        return items.filter((c) => passesRange(c.qualityScore ?? 0, f));
    }, [items, qualityRange]);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const data = await listChallenges(filter === "all" ? undefined : filter);
            setItems(data);
        } catch (e) {
            toast.error("Failed to load library");
        } finally {
            setLoading(false);
        }
    }, [filter]);

    useEffect(() => {
        load();
    }, [load]);

    useEffect(() => {
        let cancelled = false;
        getContext(3)
            .then((ctx) => {
                if (!cancelled) setContext(ctx);
            })
            .catch(() => {});
        return () => {
            cancelled = true;
        };
    }, []); // run once on mount; getContext + setContext are stable

    return (
        <AppShell liveContext={context}>
            <div className="space-y-6">
                <div className="flex flex-wrap items-end justify-between gap-3">
                    <div>
                        <h1 className="font-display font-black text-3xl md:text-4xl uppercase tracking-tighter">
                            Challenge Library
                        </h1>
                        <p className="text-white/60 text-sm mt-1">
                            Every generated challenge, ready to edit, publish, or export.
                        </p>
                    </div>
                    <div className="flex items-center gap-2">
                        <a
                            href={downloadBulkXlsxUrl(filter === "all" ? null : filter)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="font-mono text-xs uppercase tracking-widest px-3 py-1.5 border border-fa-green/40 text-fa-green hover:bg-fa-green hover:text-black transition-colors flex items-center gap-1.5"
                            data-testid="bulk-xlsx-btn"
                        >
                            <FileXls size={14} weight="bold" /> Download XLSX
                        </a>
                        <button
                            type="button"
                            onClick={async () => {
                                const status = filter === "all" ? "published" : filter;
                                try {
                                    const blob = await fetch(bulkExportZipUrl(), {
                                        method: "POST",
                                        headers: { "Content-Type": "application/json" },
                                        body: JSON.stringify({ status, includeImages: true }),
                                    }).then((r) => {
                                        if (!r.ok) throw new Error(`HTTP ${r.status}`);
                                        return r.blob();
                                    });
                                    const url = URL.createObjectURL(blob);
                                    const a = document.createElement("a");
                                    a.href = url;
                                    a.download = `challenges-${status}-${new Date().toISOString().slice(0, 10)}.zip`;
                                    a.click();
                                    URL.revokeObjectURL(url);
                                    toast.success("ZIP downloaded");
                                } catch (e) {
                                    toast.error("ZIP export failed — no challenges in this status?");
                                }
                            }}
                            className="font-mono text-xs uppercase tracking-widest px-3 py-1.5 border border-fa-cream/40 text-fa-cream hover:bg-fa-cream hover:text-black transition-colors flex items-center gap-1.5"
                            data-testid="bulk-zip-btn"
                        >
                            <FileZip size={14} weight="bold" /> Download ZIP
                        </button>
                        <div className="flex gap-1" data-testid="library-filters">
                            {FILTERS.map((f) => (
                                <button
                                    key={f.id}
                                    onClick={() => setFilter(f.id)}
                                    className={`font-mono text-xs uppercase tracking-widest px-3 py-1.5 border transition-colors ${
                                        filter === f.id
                                            ? "border-fa-yellow text-fa-yellow bg-fa-yellow/10"
                                            : "border-white/10 text-white/70 hover:border-white/30"
                                    }`}
                                    data-testid={`filter-${f.id}`}
                                >
                                    {f.label}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>

                {loading ? (
                    <div className="text-white/40 font-mono text-sm">Loading…</div>
                ) : items.length === 0 ? (
                    <EmptyState />
                ) : (
                    <>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="library-quality-filter-row">
                            <RangeFilter
                                id="library-quality"
                                testid="library-quality-filter"
                                label="Quality score"
                                minValue={0}
                                maxValue={100}
                                step={1}
                                selectedMin={qualityRange.selectedMin}
                                selectedMax={qualityRange.selectedMax}
                                format="number"
                                unit=""
                                onChange={setQualityRange}
                                defaults={{ selectedMin: 0, selectedMax: 100 }}
                            />
                            <div className="flex items-center justify-end font-mono text-[11px] text-white/50" data-testid="library-count">
                                {filteredItems.length} of {items.length} challenges shown
                            </div>
                        </div>
                        {filteredItems.length === 0 ? (
                            <div className="border border-dashed border-white/10 p-8 text-center font-mono text-xs text-white/40">
                                No challenges match the quality filter. Reset to widen the range.
                            </div>
                        ) : (
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                {filteredItems.map((c, i) => (
                                    <ChallengeCard key={c.id} challenge={c} onClick={setSelected} index={i} />
                                ))}
                            </div>
                        )}
                    </>
                )}
            </div>

            <ChallengeDetail
                challenge={selected}
                open={!!selected}
                onOpenChange={(v) => !v && setSelected(null)}
                onChanged={load}
            />
        </AppShell>
    );
}

function EmptyState() {
    return (
        <div className="border border-dashed border-white/10 p-10 text-center">
            <div className="font-mono text-[10px] uppercase tracking-widest text-white/40">
                Empty library
            </div>
            <div className="font-display font-black text-2xl uppercase mt-2">
                No challenges yet
            </div>
            <div className="text-white/50 text-sm mt-1">
                Head to the Generator to produce your first batch.
            </div>
        </div>
    );
}
